<script setup>
import { useRouter } from "vue-router";
import AccessDeniedModal from "@/components/User/AccessDeniedModal.vue";
import { ref, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useDraggableModal } from "@/composables/useDraggableModal";

const userStore = useUserStore();
const router = useRouter();
const showAccessDenied = ref(false);

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  planName: {
    type: String,
    default: "",
  },
  isManaged: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente não desmonta entre
// aberturas (só alterna `show`), então o watch recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

function handleClose() {
  emit("close");
}

function handleUpgrade() {
  if (userStore.currentUser?.loginContext === "managed") {
    showAccessDenied.value = true;
    return;
  }
  emit("close");
  router.push({ name: "UpgradePage" });
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[10000] flex items-start justify-center bg-black/20 pt-16 sm:pt-20"
      @keydown.esc.stop.prevent="handleClose"
      tabindex="0"
    >
      <div
        ref="panelRef"
        class="w-full max-w-lg rounded-2xl bg-surface px-6 py-5 shadow-2xl text-[15px] sm:text-base"
        :style="panelStyle"
        @click.stop
      >
        <!-- Cabeçalho -->
        <div
          class="mb-4 flex items-center justify-between"
          :class="{
 'cursor-move select-none touch-none': isDraggable,
            'cursor-grabbing': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h2 class="text-lg sm:text-xl font-semibold text-slate-900">
            Pagamento recusado
          </h2>
          <button
            type="button"
            class="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
            @click="handleClose"
          >
            ✕
          </button>
        </div>

        <!-- Corpo -->
        <div
          class="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-[15px] sm:text-base text-yellow-900 leading-relaxed text-justify"
        >
          <p class="font-semibold mb-2">
            <span v-if="isManaged">
              O titular da conta perdeu acesso ao plano
              <strong v-if="planName">{{ planName }}</strong
              >.
            </span>
            <span v-else>
              Não foi possível processar o pagamento da sua assinatura
              <strong v-if="planName">{{ planName }}</strong
              >.
            </span>
          </p>

          <p class="mb-3">
            <span v-if="isManaged">
              O Mercado Pago tentou cobrar a assinatura recorrente do titular,
              mas todas as tentativas foram recusadas. Como resultado, o titular
              foi migrado automaticamente para o <strong>plano Free</strong> e
              você perdeu acesso aos recursos premium.
            </span>
            <span v-else>
              O Mercado Pago tentou cobrar sua assinatura recorrente, mas todas
              as tentativas foram recusadas. Como resultado, você foi migrado
              automaticamente para o <strong>plano Free</strong>.
            </span>
          </p>

          <p class="mb-3" v-if="!isManaged">
            Isso pode ter ocorrido por falta de limite no cartão, cartão
            bloqueado ou vencido. Verifique os dados do seu cartão e atualize
            seu plano para continuar aproveitando todos os recursos.
          </p>

          <p>
            <span v-if="isManaged">
              Entre em contato com o <b>titular</b> para que ele atualize o
              plano.
            </span>
            <span v-else-if="userStore.currentUser?.loginContext === 'managed'">
              Para reativar, o <b>titular</b> deve atualizar o plano.
            </span>
            <span v-else>
              Clique em <b>Atualizar plano</b> para reativar sua assinatura.
            </span>
          </p>
        </div>

        <!-- Botões -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="btn btn-secondary text-sm"
            @click="handleClose"
          >
            Fechar
          </button>

          <button
            v-if="userStore.currentUser?.loginContext !== 'managed'"
            type="button"
            class="btn bg-[var(--brand-light)] hover:bg-[var(--brand-light-hover)] text-[color:var(--text-on-brand)] flex items-center gap-2 text-sm font-semibold"
            @click="handleUpgrade"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M13 7h6m0 0v6m0-6L10 16"
              />
            </svg>
            Atualizar plano
          </button>
        </div>
      </div>
    </div>

    <AccessDeniedModal
      :show="showAccessDenied"
      @close="showAccessDenied = false"
    />
  </Teleport>
</template>


